/**
 * OpenAIService - AI & TTS API Client for web
 * Static class for chat completions and TTS via GPT proxy
 */
import { Config } from '../../Config/Config';
import AppAuthToken from '../Security/AppAuthToken';

export type CoachMessage = { role: 'system' | 'user' | 'assistant'; content: string };
export type OpenAIVoice = 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';

const VOICE_MAP: Record<string, OpenAIVoice> = {
  male_01: 'echo',
  male_02: 'onyx',
  female_01: 'nova',
  female_02: 'shimmer',
};

export function mapToOpenAIVoice(voiceIdentifier: string): OpenAIVoice {
  return VOICE_MAP[voiceIdentifier] ?? 'nova';
}

const LOG_PREFIX = '[OpenAIService]';

export class OpenAIService {
  /**
   * Chat completion via GPT proxy endpoint
   */
  static async chat(
    messages: CoachMessage[],
    options?: { model?: string; max_completion_tokens?: number },
    signal?: AbortSignal,
  ): Promise<string> {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const token = await AppAuthToken.getAuthData();
    if (!token?.access_token) {
      throw new Error('No auth token available');
    }

    const model = options?.model || 'gemini-2.5-flash';
    const maxTokens = options?.max_completion_tokens || 512;

    const startTime = Date.now();
    console.info(`${LOG_PREFIX} chat() starting`, { model, messageCount: messages.length });

    // Timeout: abort after 30s to prevent silent hangs
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), 30_000);
    const combinedSignal = signal
      ? AbortSignal.any([signal, timeoutController.signal])
      : timeoutController.signal;

    const response = await fetch(`${Config.backend.url}/gpt/v1/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token.access_token}`,
        'X-AI-Provider': 'google',
      },
      body: JSON.stringify({
        model,
        messages,
        max_completion_tokens: maxTokens,
        stream: false,
      }),
      signal: combinedSignal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Chat API failed: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    const content = data.choices?.[0]?.message?.content?.trim() || '';
    const duration = Date.now() - startTime;

    console.info(`${LOG_PREFIX} chat() completed in ${duration}ms`, {
      responseLength: content.length,
      usage: data.usage,
    });

    return content;
  }

  /**
   * Generate speech from text using OpenAI TTS via GPT proxy.
   * Matches the native kinasticCoach app pattern: tts-1-hd model, MP3 output.
   */
  static async speak(
    text: string,
    voice: OpenAIVoice = 'nova',
    signal?: AbortSignal,
  ): Promise<ArrayBuffer> {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const startTime = Date.now();
    console.info(`${LOG_PREFIX} speak() starting`, { textLength: text.length, voice });

    const token = await AppAuthToken.getAuthData();
    if (!token?.access_token) {
      throw new Error('No auth token available');
    }

    const response = await fetch(`${Config.backend.url}/gpt/v1/audio/speech`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token.access_token}`,
        'X-AI-Provider': 'openai',
      },
      body: JSON.stringify({
        model: 'tts-1-hd',
        voice,
        input: text,
        response_format: 'mp3',
        speed: 1.0,
      }),
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TTS speak failed: ${response.status} - ${errorText}`);
    }

    const audioBuffer = await response.arrayBuffer();
    const duration = Date.now() - startTime;

    console.info(`${LOG_PREFIX} speak() completed in ${duration}ms`, {
      audioSize: audioBuffer.byteLength,
    });
    return audioBuffer;
  }

  /**
   * Generate speech from SSML — strips SSML tags and sends plain text to OpenAI TTS.
   * OpenAI TTS doesn't support SSML, so we extract text content.
   * Matches the native kinasticCoach speakSSML() pattern.
   */
  static async speakSSML(
    ssml: string,
    voice: OpenAIVoice = 'nova',
    signal?: AbortSignal,
  ): Promise<ArrayBuffer> {
    const plainText = ssml
      .replace(/<speak[^>]*>/gi, '')
      .replace(/<\/speak>/gi, '')
      .replace(/<break[^>]*\/>/gi, ', ')
      .replace(/<emphasis[^>]*>/gi, '')
      .replace(/<\/emphasis>/gi, '')
      .replace(/<prosody[^>]*>/gi, '')
      .replace(/<\/prosody>/gi, '')
      .replace(/<say-as[^>]*>/gi, '')
      .replace(/<\/say-as>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    console.info(`${LOG_PREFIX} speakSSML() converting`, {
      ssmlLength: ssml.length,
      plainTextLength: plainText.length,
      voice,
    });

    return OpenAIService.speak(plainText, voice, signal);
  }

  /**
   * Generate speech from SSML using Kinastic backend (Google TTS).
   * Uses the generate-custom endpoint which accepts dynamic SSML directly
   * and returns raw MP3 bytes — no template document needed, one round-trip.
   */
  static async speakSSMLBackend(
    ssml: string,
    voiceIdentifier: string = 'male_01',
    signal?: AbortSignal,
    language: string = 'de',
  ): Promise<ArrayBuffer> {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    // Strip SSML tags and send as plain text — Neural2 voices reject invalid SSML,
    // and the AI doesn't always produce perfectly valid SSML.
    const plainText = ssml
      .replace(/<speak[^>]*>/gi, '')
      .replace(/<\/speak>/gi, '')
      .replace(/<break[^>]*\/>/gi, '. ')
      .replace(/<emphasis[^>]*>/gi, '')
      .replace(/<\/emphasis>/gi, '')
      .replace(/<prosody[^>]*>/gi, '')
      .replace(/<\/prosody>/gi, '')
      .replace(/<say-as[^>]*>/gi, '')
      .replace(/<\/say-as>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    const startTime = Date.now();
    console.info(`${LOG_PREFIX} speakSSMLBackend() starting`, {
      originalLength: ssml.length,
      plainTextLength: plainText.length,
      voiceIdentifier,
      language,
      textPreview: plainText.substring(0, 80),
    });

    const token = await AppAuthToken.getAuthData();
    if (!token?.access_token) {
      throw new Error('No auth token available');
    }

    const response = await fetch(`${Config.backend.url}/coach/text-to-speech/generate-custom`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token.access_token}`,
      },
      body: JSON.stringify({
        identifier: 'ai_coach_dynamic',
        language,
        voiceIdentifier,
        googleTtsRequest: {
          input: { text: plainText },
          audioConfig: { audioEncoding: 'MP3', speakingRate: 1.0, pitch: 0 },
          voice: { languageCode: language, name: '', gender: 'MALE' },
        },
      }),
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`TTS generate-custom failed: ${response.status} - ${errorText}`);
    }

    const audioBuffer = await response.arrayBuffer();
    const duration = Date.now() - startTime;

    console.info(`${LOG_PREFIX} speakSSMLBackend() completed in ${duration}ms`, {
      audioSize: audioBuffer.byteLength,
    });
    return audioBuffer;
  }
}
